import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Checkbox,
  Button,
  Typography,
  MenuItem,
  Select,
  Divider,
  RadioGroup,
  FormGroup,
  FormControlLabel,
  FormControl,
  Radio,
  Input,
  FormHelperText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InputMask from 'react-input-mask';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "../ShoppingCart/ShoppingCartStyle.scss";
import PaymentGrid from "../common/Payment/Payment";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useCartCalculations } from "../../hooks/useCartCalculations";
import { Link, useNavigate } from "react-router-dom";
import Paypal from './Paypal/Paypal'
import { US_STATES } from '../../util/Mock';
import { useDispatch, useSelector } from 'react-redux';
import { processStripePaymentRequest } from '../../redux/actions/CartActions';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { userLogin } from "../../redux/actions";
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import { encryptData } from "../../util/crypto";
import './CheckoutForm.scss'
import {
  validateField as validateFieldUtil,
  validatePassword,
  validateConfirmPassword,
  formatDateToYYYYMMDD
} from '../../util/Validation';
import PasswordInput from "../common/PasswordInput/PasswordInput";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

const initialErrorFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  dlNumber: "",
  dlState: "",
  dob: "",
  password: "",
  confirmPassword: "",
  affiliated: "",
  cardNumber: '',
  cardName: '',
  cardExpiry: '',
  cardCvc: '',
  termAndCond: '',
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function CheckoutForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { stripePaymentLoading, paymentIntent, coupon } = useSelector(state => state.cart);
  const { profileInfo, isUserLoggedIn, loading: showUserLoader, userInfo } = useSelector(state => state.user);
  const endorsements = useSelector(state => state.wizardLaunch?.endorsements) || [];
  const moreClassSelection = useSelector(state => state.wizardLaunch?.moreClassSelection) || [];
  const directCourses = useSelector(state => state.wizardLaunch?.directCourses) || [];
  const { subtotal, discount, total } = useCartCalculations();
  const stripe = useStripe();
  const elements = useElements();
  const [open, setOpen] = useState(false);

  const inputCommonProps = {
    autoComplete: "new-password",
    'data-lpignore': "true",
    'data-form-type': "other",
    'autoCorrect': "off",
    'autoCapitalize': "off",
    'spellCheck': "false"
  };

  // Memoize total to prevent unnecessary recalculations
  const memoizedTotal = useMemo(() => total, [total]);

  // Get section data - memoized with useCallback
  const getSectionData = useCallback(() => {
    // First try endorsements
    if (endorsements && endorsements.length > 0) {
      return endorsements
        .filter(item => item.section_id)
        .map(item => ({
          section_id: parseInt(item.section_id),
          price: item.price
        }));
    }

    // If no endorsements, try moreClassSelection
    if (moreClassSelection && moreClassSelection.length > 0) {
      return moreClassSelection
        .filter(item => item.section_id && item.courseType !== "Instructor-Led")
        .map(item => ({
          section_id: parseInt(item.section_id),
          price: item.price
        }));
    }

    // If no moreClassSelection, try directCourses
    if (directCourses && directCourses.length > 0) {
      return directCourses
        .filter(item => item.section_id)
        .map(item => ({
          section_id: parseInt(item.section_id),
          price: item.price
        }));
    }

    return null;
  }, [endorsements.length, moreClassSelection.length, directCourses.length]);

  // Memoize section data
  const memoizedSectionData = useMemo(() => getSectionData(), [getSectionData]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    // Update amount when total changes
    setAmount(memoizedTotal);
    // Update section data when available
    if (memoizedSectionData) {
      setSectionData(memoizedSectionData);
    }
  }, [memoizedTotal, memoizedSectionData]);

  // Handle payment intent navigation with proper data formatting
  useEffect(() => {
    if (paymentIntent?.status === 200) {
      navigate('/paymentsuccess');
    }
  }, [paymentIntent, navigate]);

  // State for form values
  const [formValues, setFormValues] = useState({
    firstName: profileInfo?.usr_fname || "",
    middleName: profileInfo?.usr_mname || "",
    lastName: profileInfo?.usr_lname || "",
    email: profileInfo?.usr_email || "",
    phone: profileInfo?.usr_phone || "",
    address: profileInfo?.address || "",
    city: profileInfo?.usr_city || "",
    state: profileInfo?.usr_state || "",
    zipCode: profileInfo?.usr_zip || "",
    dlNumber: profileInfo?.license_number || "",
    dlState: profileInfo?.license_state || "",
    dob: profileInfo?.dob || "",
    password: profileInfo?.usr_password || "",
    confirmPassword: profileInfo?.usr_password || "",
    affiliated: profileInfo?.us_military?.toLowerCase() || "",
    paymentMethod: "",
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCvc: '',
    termAndCond: '',
    section_data: ''
  });

  const [loginDetails, setLoginDetails] = useState({
    email: "",
    login_password: ""
  });

  const [loginDetailsErrors, setLoginDetailsErrors] = useState({
    email: "",
    login_password: "",
  });
  // State for form errors
  const [formErrors, setFormErrors] = useState(initialErrorFormState);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [amount, setAmount] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showStudentLoginForm, setShowStudentLoginForm] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sectionData, setSectionData] = useState(null);

  const paymentContentRef = useRef(null);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  useEffect(() => {
    if (paymentContentRef.current && isUserLoggedIn) {
      paymentContentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center' 
      });
  }
    setFormErrors(initialErrorFormState);
    setFormValues((prev) => ({
      ...prev,
      firstName: profileInfo?.usr_fname || "",
      middleName: profileInfo?.usr_mname || "",
      lastName: profileInfo?.usr_lname || "",
      email: profileInfo?.usr_email || "",
      phone: profileInfo?.usr_phone || "",
      address: profileInfo?.address || "",
      city: profileInfo?.usr_city || "",
      state: profileInfo?.usr_state || "",
      zipCode: profileInfo?.usr_zip || "",
      dlNumber: profileInfo?.license_number || "",
      dlState: profileInfo?.license_state || "",
      dob: profileInfo?.dob || "",
      password: profileInfo?.usr_password || "",
      confirmPassword: profileInfo?.usr_password || "",
      affiliated: profileInfo?.us_military?.toLowerCase() || "",
    }));
  }, [profileInfo]);

  const handleStripePayment = async (event) => {
    event.preventDefault();
    const session_id = userInfo?.session_id || (uuidv4());
    // const session_id = uuidv4();
    
    try {
      if (!stripe || !elements || !validatePasswords() || !isFormValid()) {
        console.error('Stripe has not loaded yet');
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error('Error submitting payment form:', submitError);
        toast.error(submitError.message || 'Error processing payment');
        return;
      }

      // Calculate final total including all discounts

      dispatch(processStripePaymentRequest({
        stripe,
        elements,
        amount: total,
        currency: 'usd',
        session_id: session_id,
        coupon_code: coupon?.code || '',
        billingDetails: {
          name: `${formValues.firstName} ${formValues.lastName}`,
          email: formValues.email,
          phone: formValues.phone,
          address: {
            line1: formValues.address,
            city: formValues.city,
            state: formValues.state,
            postal_code: formValues.zipCode
          }
        },
        formValues: {
          ...formValues,
          dob: formatDateToYYYYMMDD(formValues.dob),
          section_data: sectionData,
          proceed: isUserLoggedIn
        },
      }));

    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Error processing payment');
    }
  };

  const handleInputChange = event => {
    const { name, value } = event.target;
    
    if (name === 'phone') {
      // Allow only numbers and hyphens
      const validInput = value.slice(0, 12).replace(/[^0-9-]/g, '');
   
      setFormValues(prev => ({
        ...prev,
        [name]: validInput
      }));
      validateField(name, validInput);
      return;
    }  
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate field immediately on change
    validateField(name, value);
    
    // Special handling for password/confirm password
    if (name === 'password' || name === 'confirmPassword') {
      validatePasswords();
    }
  };

  const handlePasswordInputChange = ({ name, value }) => {
    // Update the form values
    const updatedValues = {
      ...formValues,
      [name]: value
    };
    
    // Update the state
    setFormValues(updatedValues);

    // Validate the current field
    const error = validateFieldUtil(
      name,
      value,
      { ...updatedValues, isUserLoggedIn },
      false
    );

    // Update errors state for the current field
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // If this is the password field, also validate confirmPassword
    if (name === 'password') {
      const confirmError = validateConfirmPassword(
        value,
        updatedValues.confirmPassword,
        isUserLoggedIn
      );
      setFormErrors(prev => ({
        ...prev,
        confirmPassword: confirmError
      }));
    }

    // If this is the confirmPassword field, validate it against password
    if (name === 'confirmPassword') {
      const confirmError = validateConfirmPassword(
        updatedValues.password,
        value,
        isUserLoggedIn
      );
      setFormErrors(prev => ({
        ...prev,
        confirmPassword: confirmError
      }));
    }
  };

  const handleLoginBlur = event => {
    const { name, value } = event.target;
    validateField(name, value, true);
  };

  const handleLoginInputPasswordChange = ({ name, value }) => {
    setLoginDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value, true);
  };
  
  const handleLoginPasswordBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value, true);
  };


  // Handle blur for form fields
  const handleBlur = (event) => {
    const { name, value } = event.target;
    if (name === 'password' || name === 'confirmPassword') {
      validatePasswords();
    } else {
      validateField(name, value);
    }
  };

  const handleLoginInputEmailChange = (event) => {
    const { name, value } = event.target;
    setLoginDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value, true);
  };

  const handleStripeElementChange = (event, elementType) => {
    setFormValues(prev => ({
      ...prev,
      [elementType]: event.complete
    }));
    setFormErrors(prev => ({
      ...prev,
      [elementType]: event.error ? event.error.message : ''
    }));
  };

  const handleButtonClick = () => {
    navigate('/shoppingcart');
  };

  const handlecreditcardShow = (e) => {
    setPaymentMethod(e.target.value);
  };

  const validateField = (name, value, isLogin = false) => {
    const error = validateFieldUtil(
      name,
      value,
      { ...formValues, isUserLoggedIn },
      isLogin
    );

    if (isLogin) {
      setLoginDetailsErrors(prev => ({
        ...prev,
        [name]: error
      }));
    } else {
      setFormErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
    return error;
  };

  const validatePasswords = () => {
    const passwordError = validatePassword(formValues.password, isUserLoggedIn);
    const confirmPasswordError = validateConfirmPassword(
      formValues.password,
      formValues.confirmPassword,
      isUserLoggedIn
    );
    
    setFormErrors(prev => ({
      ...prev,
      password: passwordError,
      confirmPassword: confirmPasswordError
    }));

    return !passwordError && !confirmPasswordError;
  };

  const handlePaymentMethodChange = (event) => {
    // First, validate all fields including password confirmation
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'address',
      'city',
      'state',
      'zipCode',
      'affiliated',
      'password',
      'confirmPassword'
    ];

    let hasErrors = false;
    const newErrors = { ...formErrors };

    // Check required fields
    requiredFields.forEach(field => {
      if (!formValues[field]?.toString().trim()) {
        newErrors[field] = 'This field is required';
        hasErrors = true;
      } else if (field === 'password' || field === 'confirmPassword') {
        // Explicitly validate password fields
        const error = validateFieldUtil(
          field,
          formValues[field],
          { ...formValues, isUserLoggedIn },
          false
        );
        if (error) {
          newErrors[field] = error;
          hasErrors = true;
        }
      }
    });

    // Special check for password match
    if (formValues.password && formValues.confirmPassword && 
        formValues.password !== formValues.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      hasErrors = true;
    }

    // Update error states if there are any validation errors
    if (hasErrors) {
      setFormErrors(newErrors);
      toast.error('Please fix all validation errors before selecting a payment method');
      return;
    }

    // If no errors, proceed with payment method change
    setPaymentMethod(event.target.value);
    const session_id = userInfo?.session_id || (uuidv4());
   // const session_id = uuidv4();

    setFormValues(prev => ({
      ...prev,
      paymentMethod: event.target.value,
      section_data: sectionData,
      session_id: session_id,
      currency: 'usd'
    }));
  };

  const isFormValid = () => {
    // Ensure there are no form errors
    const noErrors = Object.values(formErrors).every((error) => error === "");

    // List of required fields
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'address',
      'city',
      'state',
      'zipCode',
      'password',
      'confirmPassword',
      'paymentMethod',
      'termAndCond',
    ];

    // Check if all required fields are filled
    const allRequiredFieldsFilled = requiredFields.every(
      field => formValues[field] && formValues[field].toString().trim() !== ""
    );

    // Additional check for card details if payment method is card
    const isCardPayment = formValues.paymentMethod === 'CreditCard';
    const cardFieldsFilled = !isCardPayment || (
      formValues.cardNumber !== "" &&
      formValues.cardName?.trim() !== "" &&
      formValues.cardExpiry !== "" &&
      formValues.cardCvc !== ""
    );

    return noErrors && allRequiredFieldsFilled && cardFieldsFilled;
  };

  const isLoginFormValid = () => {
    const noErrors = Object.values(loginDetailsErrors).every((error) => error === "");
    const requiredFields = ["email", "login_password"]
    const allRequiredFieldsFilled = requiredFields.every(
      field => loginDetails[field] && loginDetails[field].toString().trim() !== ""
    );
    return noErrors && allRequiredFieldsFilled
  }
  const handleLogin = event => {
    event.preventDefault();
    dispatch(userLogin({email: loginDetails.email, password: loginDetails.login_password}, navigate, moreClassSelection, endorsements, directCourses))
  }

  const editableFieldsRef = useRef(new Set());

  const isFieldEditable = (fieldName) => {
    if (!isUserLoggedIn) return true;
  
    if (formErrors[fieldName]) {
      editableFieldsRef.current.add(fieldName);
      return true;
    }
  
    return editableFieldsRef.current.has(fieldName);
  };

   const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const renderInputMask = () => {
    return (inputProps) => (
      <Input
        {...inputProps}
        name="dob"
        disabled={!isFieldEditable("dob")}
        autoComplete="new-password"
        inputProps={{
          ...inputCommonProps
        }}
      />
    );
  };

  return (
    <div className="shopingCart-wrap">
      <div className="heading-sec">
        <Button className="back-btn" onClick={handleButtonClick}>
          <NavigateBeforeIcon />
          Back
        </Button>
        <Typography level="h1" className="heading">
          Checkout
        </Typography>
      </div>
      {!isUserLoggedIn && (
        <>
          <div className="login_wrapper">
            {showStudentLoginForm ? (              
              <form onSubmit={handleLogin} className="login_wrapper__form" autoComplete="off" spellCheck="false">
                <div style={{ height: 0, width: 0, opacity: 0, position: 'absolute', left: '-9999px', overflow: 'hidden' }}>
                  <input type="text" name="email" autoComplete="username" />
                  <input type="password" name="password" autoComplete="current-password" />
                </div>
                <Typography
                level="h2"
                className="heading-med"
                textAlign="center"
              >
                Student Login
              </Typography>
              <div className="login-wrap">                  <FormControl
                variant="standard"
                className="form-control"
                error={!!loginDetailsErrors.email}
              >
                <label>Email *</label>                    <Input
                  id="student_login_email"
                  name="student_email"
                  value={loginDetails.email}
                  onChange={(e) => handleLoginInputEmailChange({...e, target: {...e.target, name: 'email'}})}
                  onBlur={handleLoginBlur}
                  disableUnderline
                  type="text"
                  autoComplete="off"
                  inputMode="email"
                  data-private="true"
                  inputProps={{
                    autoComplete: "new-password",
                    'data-lpignore': "true",
                    'data-form-type': "other",
                    'autoCapitalize': "off",
                    'spellCheck': "false",
                    'aria-autocomplete': "none",
                    'data-autocompletetype': "off",
                    'aria-label': "Email address for login"
                  }}
                />
                <FormHelperText>{loginDetailsErrors.email}</FormHelperText>
              </FormControl>
                {/* <FormControl
                  variant="standard"
                  className="form-control password-field"
                  error={!!loginDetailsErrors.password}
                >
                  <label>Password *</label>
                  <Input name="password"
                    // type={showPassword ? "text" : "password"}
                    type='text'
                    value={loginDetails.password}
                    onChange={handleLoginInputEmailChange}
                    onBlur={handleLoginBlur}
                    disableUnderline
                    autoComplete="new-password"
                    inputProps={{
                      ...inputCommonProps
                    }}
                      endAdornment={
                        <InputAdornment
                          position="end"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "calc(50% - 20px)",
                            transform:
                              "translateY(calc(50% - 20px)) translateX(-80%)",
                          }}
                        >
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                            style={{ padding: "4px" }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    <FormHelperText>
                      {loginDetailsErrors.password}
                    </FormHelperText>
                  </FormControl> */}


                   <div style={{marginTop:'10px', marginBottom:'10px' }}>
                     <PasswordInput
                      name="login_password"
                      formErrors={loginDetailsErrors}
                      inputCommonProps={inputCommonProps}
                      isUserLoggedIn={isUserLoggedIn}
                      onPasswordChange={handleLoginInputPasswordChange}
                      label="Password"
                      onBlur={handleLoginPasswordBlur}
                      disableUnderline
                    />
                   </div>

                </div>
                <div className="btn-wrap login-btn">
                  <Button
                    className="btn-pay-now login"
                    type="submit"
                    disabled={!isLoginFormValid() || showUserLoader}
                  >
                    {showUserLoader ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Login"
                    )}
                  </Button>
                  <Link
                    className="forgot-password-link"
                    to={`${process.env.REACT_APP_STUDENT_DASHBOARD_URL}/forgot-password`}
                    target="_blank"
                  >
                    Forgot password?
                  </Link>
                </div>
              </form>
            ) : (
              <Typography level="h2" className="heading-med" textAlign="center">
                Already have an account?{" "}
                <span
                  className="login-link"
                  role="button"
                  onClick={() => setShowStudentLoginForm(true)}
                >
                  Login
                </span>
              </Typography>
            )}
          </div>
          <div className="divider">
            <Divider />
          </div>
        </>
      )}

      <div className="grid-container">
        <div className="left-grid-container">
          <div className="selected-sec">            <form onSubmit={handleStripePayment} autoComplete="off" data-lpignore="true" data-form-type="other" spellCheck="false">
            <div style={{ display: 'none', height: 0, width: 0, opacity: 0 }}>
              <input type="text" id="prevent_autofill" name="hidden_username" autoComplete="new-password" />
              <input type="email" id="prevent_autofill" name="hidden_email" autoComplete="new-password" />
              <input type="password" id="prevent_autofill" name="hidden_password" autoComplete="new-password" />
            </div>
            {/* Personal Information Section */}
            <Typography level="h2" className="heading-med">
              Personal Information
            </Typography>
            <div className="form-wrap">
              <FormControl
                variant="standard"
                className="form-control"
                error={!!formErrors.firstName}
              >
                <label>First Name *</label>                  <Input
                  name="firstName"
                  data-testid="firstName"
                  value={formValues.firstName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("firstName")}
                  autoComplete="new-password"
                  inputProps={{
                    ...inputCommonProps,
                    'data-autocompletetype': "none"
                  }}
                />
                <FormHelperText>{formErrors.firstName}</FormHelperText>
              </FormControl>
              <FormControl variant="standard" error={!!formErrors.middleName}>
                <label>Middle Name</label>
                <Input
                  name="middleName"
                  value={formValues.middleName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("middleName")}
                  autoComplete="chrome-off"
                  inputProps={{
                    ...inputCommonProps
                  }}
                />
                <FormHelperText>{formErrors.middleName}</FormHelperText>
              </FormControl>
              <FormControl variant="standard" error={!!formErrors.lastName}>
                <label>Last Name *</label>
                <Input
                  name="lastName"
                  value={formValues.lastName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("lastName")}
                  autoComplete="chrome-off"
                  inputProps={{
                    ...inputCommonProps
                  }}
                />
                <FormHelperText>{formErrors.lastName}</FormHelperText>
              </FormControl>
            </div>
            <div className="form-wrap-full">
              <FormControl
                variant="standard"
                className="form-control"
                error={!!formErrors.email}
              >
                <label>Email *</label>                  <Input
                  name="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={isUserLoggedIn}
                  autoComplete="new-password"
                  inputProps={{
                    ...inputCommonProps
                  }}
                />
                <FormHelperText>{formErrors.email}</FormHelperText>
              </FormControl>
              <Typography className="info-txt">
                <InfoOutlinedIcon />
                This will be your username for accessing your course.
              </Typography>
            </div>
            <div className="form-wrap-full">
              <FormControl
                variant="standard"
                className="form-control"
                error={!!formErrors.phone}
              >
                <label>Phone *</label>                  <Input
                  name="phone"
                  value={formValues.phone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("phone")}
                  autoComplete="new-password"
                  inputProps={{
                    ...inputCommonProps,
                    maxLength: 12
                  }}
                />
                <FormHelperText>{formErrors.phone}</FormHelperText>
              </FormControl>
            </div>
            <div className="form-wrap-full">
              <FormControl
                variant="standard"
                className="form-control"
                error={!!formErrors.address}
              >
                <label>Address *</label>
                <Input
                  name="address"
                  value={formValues.address}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("address")}
                  autoComplete="chrome-off"
                  inputProps={{
                    ...inputCommonProps
                  }}
                />
                <FormHelperText>{formErrors.address}</FormHelperText>
              </FormControl>
            </div>
            <div className="form-wrap">
              <FormControl
                variant="standard"
                className="form-control"
                error={!!formErrors.city}
              >
                <label>City *</label>
                <Input
                  name="city"
                  value={formValues.city}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("city")}
                  autoComplete="chrome-off"
                  inputProps={{
                    ...inputCommonProps
                  }}
                />
                <FormHelperText>{formErrors.city}</FormHelperText>
              </FormControl>
              <FormControl variant="standard" error={!!formErrors.state} className='select-box'>
                <label>State *</label>
                <Select
                  name="state"
                  value={formValues.state}
                  IconComponent={ArrowDropDownCircleIcon}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("state")}
                  onChange={handleInputChange}
                  MenuProps={{
                    PaperProps: {
                      className: "custom-dropdown-menu",
                      sx: {
                        maxHeight: "300px",
                      },
                    },
                  }}
                >
                  <MenuItem value="" >Select State</MenuItem>
                  {US_STATES.map((state) => (
                    <MenuItem className="menu-item-hover" key={state?.name} value={state?.shortName}>
                      {state?.name}
                    </MenuItem>
                  ))}
                </Select>

                <FormHelperText>{formErrors.state}</FormHelperText>
              </FormControl>
              <FormControl variant="standard" error={!!formErrors.zipCode}>
                <label>ZIP Code *</label>
                <Input
                  name="zipCode"
                  value={formValues.zipCode}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("zipCode")}
                  type="text"
                  autoComplete="chrome-off"
                  inputProps={{
                    ...inputCommonProps,
                    maxLength: 10,
                  }}
                />
                <FormHelperText>{formErrors.zipCode}</FormHelperText>
              </FormControl>
            </div>
            <div className="form-wrap">
              <FormControl
                variant="standard"
                className="form-control"
                error={!!formErrors.dlNumber}
              >
                <label>DL #</label>
                <Input
                  name="dlNumber"
                  value={formValues.dlNumber}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("dlNumber")}
                  autoComplete="chrome-off"
                  inputProps={{
                    ...inputCommonProps
                  }}
                />
                <FormHelperText>{formErrors.dlNumber}</FormHelperText>
              </FormControl>
              <FormControl variant="standard" error={!!formErrors.dlState} className='select-box'>
                <label>DL State</label>
                <Select
                  name="dlState"
                  value={formValues.dlState}
                  IconComponent={ArrowDropDownCircleIcon}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("dlState")}
                  onChange={handleInputChange}
                  MenuProps={{
                    PaperProps: {
                      className: "custom-dropdown-menu",
                      sx: {
                        maxHeight: "300px",

                      },
                    },
                  }}
                >
                  {US_STATES.map((state) => (
                    <MenuItem key={state?.name} value={state?.shortName} className="menu-item-hover" >
                      {state?.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{formErrors.dlState}</FormHelperText>
              </FormControl>
              <FormControl variant="standard" error={!!formErrors.dob}>
                <label>DOB</label>                  <InputMask
                  mask="99-99-9999"
                  value={formValues.dob}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("dob")}
                  placeholder="mm-dd-yyyy"
                >
                  {renderInputMask()}
                </InputMask>
                <FormHelperText>{formErrors.dob}</FormHelperText>
              </FormControl>
            </div>
            <div className="form-wrap-full">
              <Typography className="dl-info-txt" onClick={handleClickOpen}>
                <InfoOutlinedIcon />
                Why do we ask for your driver's license information and date
                of birth?
              </Typography>
            </div>
            <div className="form-wrap-full">
              <FormControl variant="standard" error={!!formErrors.affiliated} className='select-box'>
                <label>Are you affiliated with US Millitary ? *</label>
                <Select
                  name="affiliated"
                  value={formValues.affiliated}
                  IconComponent={ArrowDropDownCircleIcon}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!isFieldEditable("affiliated")}
                >
                  <MenuItem className="menu-item-hover" value="yes">Yes</MenuItem>
                  <MenuItem className="menu-item-hover" value="no">No</MenuItem>
                </Select>
                <FormHelperText>{formErrors.affiliated}</FormHelperText>
              </FormControl>
            </div>
            {!isUserLoggedIn && (
              <>
                <Divider />
                <Typography level="h2" className="heading-med mt-48">
                  Password
                </Typography>
                <div className="form-wrap-full">
                    <PasswordInput
                      name="password"
                      formErrors={formErrors}
                      inputCommonProps={inputCommonProps}
                      isUserLoggedIn={isUserLoggedIn}
                      onPasswordChange={handlePasswordInputChange}
                      label="Password"
                      onBlur={handleBlur}
                    />

                  {/* <FormControl
                    variant="standard"
                    className="form-control password-field"
                    error={!!formErrors.password}
                  >
                    <label>Password *</label>                    <Input
                      name="password"
                      // type={showPassword ? "text" : "password"}
                       type='password'
                      value={formValues.password}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isUserLoggedIn}
                      autoComplete="chrome-off"
                      inputProps={{
                        ...inputCommonProps
                      }}
                      endAdornment={
                        <InputAdornment
                          position="end"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%) translateX(-80%)",
                          }}
                        >
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                            style={{ padding: "4px" }}
                          >
                            {showPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    <FormHelperText>{formErrors.password}</FormHelperText>
                  </FormControl> */}
                </div>
                <div className="form-wrap-full">

                   <PasswordInput
                      name="confirmPassword"
                      formErrors={formErrors}
                      inputCommonProps={inputCommonProps}
                      isUserLoggedIn={isUserLoggedIn}
                      onPasswordChange={handlePasswordInputChange}
                      label="Confirm Password"
                      onBlur={handleBlur}
                    />


                  {/* <FormControl
                    variant="standard"
                    className="form-control password-field"
                    error={!!formErrors.confirmPassword}
                  >
                    <label>Confirm Password *</label>
                    <Input
                      name="confirmPassword"
                       type='text'
                      // type={showConfirmPassword ? "text" : "password"}
                      value={formValues.confirmPassword}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isUserLoggedIn}
                      autoComplete="chrome-off"
                      inputProps={{
                        ...inputCommonProps
                      }}
                      endAdornment={
                        <InputAdornment
                          position="end"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%) translateX(-80%)",
                          }}
                        >
                          <IconButton
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                            style={{ padding: "4px" }}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    <FormHelperText>
                      {formErrors.confirmPassword}
                    </FormHelperText>
                  </FormControl> */}
                </div>
                <Divider />
              </>
            )}
            {/* Payment Section */}
            <Typography level="h2" className="heading-med">
              Payment Methods
            </Typography>
            <div className="radio-btn-wrap" ref={paymentContentRef}>
              <RadioGroup
                className="radio-btn-group"
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="paymentMethod"
                value={formValues.paymentMethod}
                onChange={handlePaymentMethodChange}
              >
                <div className="credit-card-wrap">
                  <FormControlLabel
                    control={<Radio />}
                    label="Credit Card"
                    value="CreditCard"
                  />
                  {formValues.paymentMethod === "CreditCard" && (
                    <div className="credit-card-form">
                      <div className="form-wrap-full">
                        <FormControl
                          variant="standard"
                          className="form-control"
                        >
                          <label>Credit Card Number</label>
                          <CardNumberElement
                            options={{
                              ...CARD_ELEMENT_OPTIONS,
                              style: {
                                ...CARD_ELEMENT_OPTIONS.style,
                                base: {
                                  ...CARD_ELEMENT_OPTIONS.style.base,
                                  '::placeholder': {
                                    color: '#aab7c4',
                                    opacity: 1
                                  }
                                }
                              },
                              disableLink: true
                            }}
                            className="stripe-element"
                            id="creditCardNo"
                            name="cardNumber"
                            autoComplete="cc-number"
                            // disabled={processing || succeeded}
                            onChange={(event) =>
                              handleStripeElementChange(event, "cardNumber")
                            }
                          />
                        </FormControl>
                      </div>
                      <div className="form-wrap-full">
                        <FormControl
                          variant="standard"
                          className="form-control"
                        >
                          <label>Name on Card</label>
                          <Input
                            id="cardName"
                            fullWidth
                            placeholder="Name on card"
                            name="cardName" 
                            onChange={handleInputChange}
                            value={formValues.cardName || ""}
                            autoComplete="cc-name"
                            inputProps={{
                              ...inputCommonProps,
                              autoComplete: 'cc-name',
                              'data-lpignore': 'true',
                              'data-form-type': 'other',
                              'data-private': 'true',
                              'autoCorrect': 'off',
                              'spellCheck': 'false',
                              'autoCapitalize': 'off',
                              'aria-autocomplete': 'none',
                              'aria-label': 'Credit card name',
                              'aria-required': 'true'
                            }}
                            // disabled={processing || succeeded}
                          />
                        </FormControl>
                      </div>
                      <div className="form-wrap">
                        <FormControl
                          variant="standard"
                          className="form-control"
                        >
                          <label>MM/YY</label>
                          <CardExpiryElement
                            options={{
                              ...CARD_ELEMENT_OPTIONS,
                              style: {
                                ...CARD_ELEMENT_OPTIONS.style,
                                base: {
                                  ...CARD_ELEMENT_OPTIONS.style.base,
                                  '::placeholder': {
                                    color: '#aab7c4',
                                    opacity: 1
                                  }
                                }
                              }
                            }}
                            // disabled={processing || succeeded}
                            className="stripe-element"
                            onChange={(event) =>
                              handleStripeElementChange(event, "cardExpiry")
                            }
                            id="cardExpiry"
                            name="cardExpiry"
                            label="Expiry Date"
                            autoComplete="cc-exp"
                            inputProps={{
                              'autoComplete': 'cc-exp',
                              'data-lpignore': 'true',
                              'data-form-type': 'other',
                              'data-private': 'true',
                              'aria-label': 'Card expiration date',
                              'aria-required': 'true'
                            }}
                          />
                        </FormControl>
                        <FormControl variant="standard">
                          <label>CVV </label>
                          <CardCvcElement
                            options={{
                              ...CARD_ELEMENT_OPTIONS,
                              style: {
                                ...CARD_ELEMENT_OPTIONS.style,
                                base: {
                                  ...CARD_ELEMENT_OPTIONS.style.base,
                                  '::placeholder': {
                                    color: '#aab7c4',
                                    opacity: 1
                                  }
                                }
                              }
                            }}
                            onChange={(event) =>
                              handleStripeElementChange(event, "cardCvc")
                            }
                            id="cardCvc"
                            name="cardCvc"
                            // disabled={processing || succeeded}
                            className="stripe-element"
                            autoComplete="cc-csc"
                            inputProps={{
                              'autoComplete': 'cc-csc',
                              'data-lpignore': 'true',
                              'data-form-type': 'other',
                              'data-private': 'true',
                              'aria-label': 'Card security code',
                              'aria-required': 'true'
                            }}
                          />
                        </FormControl>
                      </div>
                    </div>
                  )}
                </div>
                <div className="paypal-wrap">
                  <FormControlLabel
                    control={<Radio />}
                    label="PayPal"
                    value="paypal"
                  />
                  {paymentMethod === "paypal" && (
                    <Paypal
                      checkoutData={{
                        ...formValues,
                        dob: formatDateToYYYYMMDD(formValues.dob),
                        amount: total,
                      }}
                      isFormValid={isFormValid()}
                    />
                  )}
                </div>
              </RadioGroup>
            </div>
            <Divider />
            <div className="btn-wrap">
              <FormGroup className="checkbox-wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formValues.termAndCond)}
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          termAndCond: e.target.checked,
                        });
                      }}
                    />
                  }
                  id="termAndCond"
                  label={
                    <>
                      {formValues.paymentMethod === "paypal" ? (
                        <>
                          I have read and agree to the &nbsp;
                          <span className="border-b text-dark">
                            <strong>Terms and Conditions</strong>
                          </span>
                        </>
                      ) : (
                        <>
                          I have read and agree to the <br />
                          <span className="border-b text-dark">
                            <strong>Terms and Conditions</strong>
                          </span>
                        </>
                      )}
                    </>
                  }
                  name="termAndCond"
                />
              </FormGroup>
              {formValues.paymentMethod !== "paypal" && (
                <Button
                  className="btn-pay-now"
                  disabled={!isFormValid() || stripePaymentLoading}
                  type="submit"
                >
                  {stripePaymentLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Pay Now"
                  )}
                </Button>
              )}
            </div>
          </form>
          </div>
        </div>
        <PaymentGrid subtotal={subtotal} discount={discount} total={total} />
      </div>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="dl-info-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: "24px",
            position: "relative",
          },
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle>
          <Typography color="#1b56bb" fontWeight={600}>
            Why do we ask for your driver's license information and date of
            birth?
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" id="dl-info-description">
            To submit your online course completion to the FMCSA, we must
            collect your license number, issuing state and date of birth.
            Entering it now is optional—but skipping it may delay your reporting
            after course completion.
          </Typography>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CheckoutForm;